import React from "react";
import "./Footer.css";
import { Link, useLocation } from "react-router-dom";
import { AiPlaygroundWordmark } from "./AiPlaygroundWordmark";
import FollowSocialStrip from "./FollowSocialStrip";
import MediaWaveWordmark from "./MediaWaveWordmark";
import FooterContactForm from "./FooterContactForm";

interface FooterProps {
  onTicketClick?: () => void;
  className?: string;
  logoText?: string;
  /** When set, renders this image as the wordmark (e.g. AI page SVG logos). */
  logoSrc?: string;
  logoTo?: string;
}

type FooterSubLink = { label: string; to?: string };

type NavCol = {
  title: string;
  to: string;
  paths: string[];
  links: FooterSubLink[];
};

const footerNav: NavCol[] = [
  {
    title: "2027 Events",
    to: "/xrp-australia",
    paths: ["/xrp-australia", "/xrp-london"],
    links: [
      { label: "Sydney", to: "/xrp-australia" },
      { label: "London", to: "/xrp-london" },
    ],
  },
  /* SHELVED PROGRAMS — column removed
  {
    title: "Programs",
    to: "/programs",
    paths: ["/programs", "/register"],
    links: [
      { label: "Institutions", to: "/programs" },
      { label: "Partners", to: "/programs#footer-register" },
    ],
  },
  */
  {
    title: "Company",
    to: "/team",
    paths: ["/team", "/careers"],
    links: [
      { label: "Team", to: "/team" },
      { label: "Careers", to: "/careers" },
    ],
  },
  {
    title: "Resources",
    to: "/resources",
    paths: ["/resources", "/blog", "/gallery", "/xrp-australia-2026-collage", "/branding"],
    links: [
      { label: "Blog", to: "/blog" },
      /* SHELVED MEDIA { label: "Media", to: "/media" }, */
      { label: "Gallery", to: "/gallery" },
      { label: "Brand assets", to: "/branding" },
    ],
  },
  {
    title: "About",
    to: "/about",
    paths: ["/about", "/about-us", "/faq"],
    links: [
      { label: "Home", to: "/" },
      { label: "Our Mission", to: "/about#our-mission" },
      { label: "FAQ", to: "/faq" },
    ],
  },
];

const Footer: React.FC<FooterProps> = ({
  onTicketClick: _onTicketClick = () => {},
  className,
  logoText,
  logoSrc,
  logoTo,
}) => {
  const location = useLocation();

  const isPathActive = (paths: string[]) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  const isMediaPages =
    location.pathname === "/media" || location.pathname.startsWith("/media/");

  const isFooterSubLinkActive = (to: string) => {
    const [pathOnly] = to.split("#");
    if (to === "/blog") {
      return location.pathname === "/blog" || location.pathname.startsWith("/blog/");
    }
    return location.pathname === pathOnly || location.pathname.startsWith(pathOnly + "/");
  };

  const useLogoImage = Boolean(logoSrc);
  const useAiPlaygroundWordmark =
    Boolean(logoText === "AI Fest 2026" && !useLogoImage && className?.includes("footer--ai"));
  const useTextLogo = Boolean(logoText) && !useLogoImage && !useAiPlaygroundWordmark;
  const resolvedLogoTo = logoTo ?? (isMediaPages ? "/media" : "/");

  return (
    <footer className={["footer", className].filter(Boolean).join(" ")}>
      <div className="footer-inner">
        <div className={["footer-top", isMediaPages ? "footer-top--no-register" : "footer-top--with-register"].join(" ")}>
          <div className="footer-main-col">
            <div className="footer-brand-col">
              <div className="footer-brand">
                <Link to={resolvedLogoTo} className="footer-logo-link">
                  {useLogoImage ? (
                    <img
                      src={logoSrc}
                      alt={logoText ?? "Wave of Innovation AI Conference"}
                      className="footer-logo footer-logo--ai"
                    />
                  ) : useAiPlaygroundWordmark ? (
                    <AiPlaygroundWordmark className="footer-logo-text" />
                  ) : useTextLogo ? (
                    <span className="footer-logo-text">
                      <span className="footer-logo-text-equals">{logoText?.charAt(0) ?? ""}</span>
                      <span className="footer-logo-text-main">{logoText?.slice(1) ?? ""}</span>
                    </span>
                  ) : isMediaPages ? (
                    <MediaWaveWordmark surface="dark" className="footer-logo footer-logo--media footer-logo--wave-wordmark" />
                  ) : (
                    <img
                      src="/woi_logos/woi+X_logo_light_bg.svg"
                      alt="Wave of Innovation"
                      className="footer-brand-mark"
                      width={3750}
                      height={975}
                      decoding="async"
                    />
                  )}
                </Link>
              </div>
              {!isMediaPages && !useLogoImage && !useTextLogo && !useAiPlaygroundWordmark ? (
                <p className="footer-tagline">
                  Flagship XRP summits in Australia and London, where institutional finance, builders, and ecosystem partners meet to shape the next chapter of digital assets.
                </p>
              ) : null}
            </div>

            <div className="footer-nav-wrap">
              <nav className="footer-nav" aria-label="Site">
                {footerNav.map((col) => (
                  <div key={col.title} className="footer-nav-col">
                    <Link
                      to={col.to}
                      className={`footer-nav-heading ${isPathActive(col.paths) ? "active" : ""}`}
                    >
                      {col.title}
                    </Link>
                    {col.links.length > 0 && (
                      <ul className="footer-nav-list">
                        {col.links.map((item) => (
                          <li key={item.label}>
                            {item.to ? (
                              <Link
                                to={item.to}
                                className={isFooterSubLinkActive(item.to) ? "active" : ""}
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <span className="footer-nav-text">{item.label}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {!isMediaPages ? (
            <div id="footer-register" className="footer-register-panel" tabIndex={-1}>
              <h2 className="footer-register-heading">Register interest</h2>
              <FooterContactForm variant="footer" />
            </div>
          ) : null}
        </div>

        <div className="footer-mid">
          <div className="footer-follow">
            <FollowSocialStrip variant="onDark" />
          </div>
          <div className="footer-meta">
            <p className="footer-meta-line footer-meta-registry">
              <span>ABN 81 669 563 679</span>
              <span className="footer-meta-registry-sep" aria-hidden>
                ·
              </span>
              <span>ACN 669 563 679</span>
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-legal">
            <Link to="/terms" className="footer-legal-link">
              Terms
            </Link>
            <span className="footer-legal-separator" aria-hidden>
              ·
            </span>
            <Link to="/disclaimer" className="footer-legal-link">
              Disclaimer
            </Link>
            <span className="footer-legal-separator" aria-hidden>
              ·
            </span>
            <Link to="/privacy" className="footer-legal-link">
              Privacy
            </Link>
          </div>
          <p className="footer-copyright">
            &copy; {new Date().getFullYear()} Wave of Innovation
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
